import React, { useMemo, useState } from "react";
import Select from "react-select";
import { tWithKeyAttr as t } from "../../../i18nHelper";
import i18n from "../../../i18n";
import styles from "../forms.module.css";
import fieldStyles from "./treatmentProtocolAssignField.module.css";
import {
  useAssignTreatmentProtocolMutation,
  useGetTreatmentProtocolsQuery,
  useGetUserTreatmentProtocolHistoryQuery,
  useGetUserTreatmentProtocolQuery,
} from "../../../services/api/treatmentProtocols.api";
import {
  TreatmentProtocol,
  TreatmentProtocolKind,
} from "../../../services/types/treatmentProtocol.types";
import { describeProtocolHeadline } from "../../../pages/treatmentProtocols/treatmentProtocolSummary";
import Modal from "../../modals/modal/Modal";
import TreatmentProtocolDetails from "../../modals/treatmentProtocolDetails/treatmentProtocolDetails";
import DeleteConfirmation from "../../modals/deleteConfirmation/deleteConfirmation";
import InfoIcon from "../../icons/InfoIcon";

interface TreatmentProtocolAssignFieldProps {
  /** Undefined while the patient is still being created. */
  userId?: number;
}

interface ProtocolOption {
  value: number;
  label: string;
  headline: string;
  protocol: TreatmentProtocol;
}

/**
 * Assigns a treatment protocol (device or regular) to a patient. Saved through its own endpoint
 * the moment it changes, so it stays out of the user DML payload.
 *
 * Kept separate from PROTOCOL_FOR_PATIENT, which is the questionnaire-protocol association.
 */
function TreatmentProtocolAssignField({ userId }: TreatmentProtocolAssignFieldProps) {
  const { data: protocols } = useGetTreatmentProtocolsQuery();
  const { data: assigned } = useGetUserTreatmentProtocolQuery(userId as number, {
    skip: !userId,
  });
  const [assignProtocol, { isLoading: saving }] = useAssignTreatmentProtocolMutation();
  const [error, setError] = useState<string | null>(null);
  const [detailsFor, setDetailsFor] = useState<TreatmentProtocol | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  /** A chosen protocol waiting for confirmation. null id means "remove the assignment". */
  const [pendingChange, setPendingChange] = useState<{ nextId: number | null; name: string } | null>(
    null,
  );

  const { data: history = [] } = useGetUserTreatmentProtocolHistoryQuery(userId as number, {
    skip: !userId || !showHistory,
  });

  /**
   * Grouped by kind rather than flattened with the kind in each label: the two kinds behave
   * differently once assigned, so the distinction has to be visible before choosing, not read
   * out of a suffix. Empty groups are dropped so a company using only one kind sees no noise.
   */
  const groupedOptions = useMemo(() => {
    const byKind = (kind: TreatmentProtocolKind): ProtocolOption[] =>
      (protocols ?? [])
        .filter((protocol) => protocol.kind === kind)
        .map((protocol) => ({
          value: protocol.id,
          label: protocol.name,
          headline: describeProtocolHeadline(protocol),
          protocol,
        }));

    return (
      [
        { kind: "device" as const, labelKey: "TREATMENT_PROTOCOL_DEVICE" },
        { kind: "regular" as const, labelKey: "TREATMENT_PROTOCOL_REGULAR" },
      ]
        .map((group) => ({ label: t(group.labelKey), options: byKind(group.kind) }))
        .filter((group) => group.options.length > 0)
    );
  }, [protocols]);

  const selected =
    groupedOptions.flatMap((group) => group.options).find((o) => o.value === assigned?.id) ?? null;

  /**
   * Assigning is not a preference, it is a clinical decision with a consequence: changing or
   * removing the protocol closes the period in force and the treatment count starts over. A
   * dropdown commits on a single click, so the confirmation is what stands between a mis-click and
   * a patient's counter silently going back to zero.
   */
  const handleSelect = (nextId: number | null) => {
    if (!userId) return;

    // Picking the protocol already in force changes nothing — no question worth asking.
    if (nextId !== null && nextId === assigned?.id) return;
    if (nextId === null && !assigned) return;

    setPendingChange({
      nextId,
      name: nextId === null
        ? assigned?.name ?? ""
        : groupedOptions.flatMap((g) => g.options).find((o) => o.value === nextId)?.label ?? "",
    });
  };

  const handleConfirmChange = async () => {
    if (!userId || !pendingChange) return;
    setError(null);
    const result = await assignProtocol({ userId, treatmentProtocolId: pendingChange.nextId });
    if ("error" in result) setError("GENERAL_ERROR");
  };

  /**
   * Re-sends the protocol already in force with a new quota. The server treats that as an edit of
   * the current period rather than a new one, so changing the number never resets the count.
   */
  const handleQuotaChange = async (raw: string) => {
    if (!userId || !assigned) return;
    setError(null);
    // Emptying the box means "no limit", which is stored as 0 rather than as null: null on this
    // path reads as "field not sent" and would leave the old quota in place, so clearing it would
    // look like it worked and change nothing.
    const parsed = raw.trim() === "" ? 0 : Number(raw);
    if (!Number.isFinite(parsed) || parsed < 0) return;

    const result = await assignProtocol({
      userId,
      treatmentProtocolId: assigned.id,
      sessionsUntilRescan: parsed,
    });
    if ("error" in result) setError("GENERAL_ERROR");
  };

  const formatDate = (value?: string | null) =>
    value ? new Date(value).toLocaleDateString(i18n.language === "he" ? "he-IL" : "en-US") : "";

  return (
    <li>
      <label htmlFor="treatmentProtocolId" className={styles.form__label}>
        {t("ASSIGN_TREATMENT_PROTOCOL")}
      </label>
      <Select<ProtocolOption>
        name="treatmentProtocolId"
        inputId="treatmentProtocolId"
        options={groupedOptions as never}
        value={selected}
        placeholder={t(userId ? "SELECT_TREATMENT_PROTOCOL" : "SAVE_PATIENT_FIRST")}
        className={styles.select}
        isClearable
        isDisabled={!userId || saving}
        formatOptionLabel={(option, formatMeta) => {
          if (formatMeta.context !== "menu") return option.label;

          // react-select turns the selected row's text white but cannot reach the nested headline,
          // so the row's own state has to drive it. selectValue is what the meta exposes.
          const isSelected = formatMeta.selectValue?.some((v) => v.value === option.value);

          return (
            <div
              className={`${fieldStyles.optionRow} ${
                isSelected ? fieldStyles.optionRow_selected : ""
              }`}
            >
              {/* onMouseDown, not onClick: react-select commits the selection on mouse-down, so
                  the event has to be stopped there or opening the details also assigns. */}
              <button
                type="button"
                className={fieldStyles.infoBtn}
                title={t("VIEW_PROTOCOL_DETAILS")}
                aria-label={t("VIEW_PROTOCOL_DETAILS")}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setDetailsFor(option.protocol);
                }}
              >
                <InfoIcon />
              </button>
              <div className={fieldStyles.optionText}>
                <div>{option.label}</div>
                <div className={fieldStyles.optionHeadline}>{option.headline}</div>
              </div>
            </div>
          );
        }}
        onChange={(value) => handleSelect(value?.value ?? null)}
      />
      {assigned && (
        <span className={fieldStyles.assignedHeadline}>{describeProtocolHeadline(assigned)}</span>
      )}

      {assigned && (
        <div className={fieldStyles.rescanBlock}>
          <label htmlFor="sessionsUntilRescan" className={styles.form__label}>
            {t("TREATMENT_SESSIONS_UNTIL_RESCAN")}
          </label>
          <input
            id="sessionsUntilRescan"
            type="number"
            min={0}
            className={styles.form__input}
            defaultValue={assigned.sessionsUntilRescan ?? ""}
            disabled={saving}
            // On blur, not on every keystroke: each save is a write to the assignment.
            onBlur={(e) => handleQuotaChange(e.target.value)}
          />

          {/* The count is what the gate enforces on save, so it is shown where the number is set. */}
          {assigned.kind === "device" && (assigned.sessionsUntilRescan ?? 0) > 0 && (
            <span
              className={
                assigned.rescanDue ? fieldStyles.rescanDue : fieldStyles.rescanProgress
              }
            >
              {/* Three numbers, spelled out. "Booked" used to be shown next to "done" without
                  saying that it means the ones still ahead — so every report filed moved a session
                  from one number to the other and both looked wrong. */}
              {assigned.rescanDue
                ? t("TREATMENT_RESCAN_DUE")
                : `${t("TREATMENT_SESSIONS_DONE")}: ${assigned.sessionsCompleted ?? 0}/${
                    assigned.sessionsUntilRescan
                  } · ${t("TREATMENT_SESSIONS_UPCOMING")}: ${assigned.sessionsScheduled ?? 0} · ${t(
                    "TREATMENT_SESSIONS_REMAINING",
                  )}: ${assigned.sessionsRemaining ?? 0}`}
            </span>
          )}

          {assigned.assignmentStartDate && (
            <span className={fieldStyles.assignedHeadline}>
              {t("TREATMENT_PROTOCOL_SINCE")} {formatDate(assigned.assignmentStartDate)}
            </span>
          )}
        </div>
      )}

      {userId && (
        <button
          type="button"
          className={fieldStyles.historyToggle}
          onClick={() => setShowHistory((open) => !open)}
        >
          {t("TREATMENT_PROTOCOL_HISTORY")}
        </button>
      )}

      {showHistory && (
        <ul className={fieldStyles.historyList}>
          {history.length === 0 && (
            <li className={fieldStyles.assignedHeadline}>{t("NO_RESULTS")}</li>
          )}
          {history.map((entry) => (
            <li key={entry.id} className={fieldStyles.historyRow}>
              <span>{entry.name}</span>
              <span className={fieldStyles.assignedHeadline}>
                {formatDate(entry.startDate)} –{" "}
                {entry.isCurrent ? t("TREATMENT_PROTOCOL_CURRENT") : formatDate(entry.endDate)}
              </span>
            </li>
          ))}
        </ul>
      )}

      {error && <span className={styles.form__error}>{t(error)}</span>}

      {pendingChange && (
        <Modal onClose={() => setPendingChange(null)}>
          <DeleteConfirmation
            onClose={() => setPendingChange(null)}
            onDelete={handleConfirmChange}
            btnText={t("CONFIRM")}
            text={
              pendingChange.nextId === null
                ? t("CONF_REMOVE_TREATMENT_PROTOCOL", { name: pendingChange.name })
                : assigned
                ? t("CONF_CHANGE_TREATMENT_PROTOCOL", {
                    current: assigned.name,
                    name: pendingChange.name,
                  })
                : t("CONF_ASSIGN_TREATMENT_PROTOCOL", { name: pendingChange.name })
            }
            // Only when something is actually being closed: on a first assignment there is no
            // count to lose, and a warning that does not apply teaches people to ignore warnings.
            warningDetail={assigned ? t("CONF_TREATMENT_PROTOCOL_RESETS_COUNT") : undefined}
          />
        </Modal>
      )}

      {detailsFor && (
        // `wide` lets the modal size to the content; without it the shared Modal is a fixed
        // 493px and the details grid scrolled sideways instead of down.
        <Modal wide onClose={() => setDetailsFor(null)}>
          <TreatmentProtocolDetails
            protocol={detailsFor}
            onClose={() => setDetailsFor(null)}
          />
        </Modal>
      )}
    </li>
  );
}

export default TreatmentProtocolAssignField;
